import { useId } from 'react';
import { Box, useTheme } from '@mui/material';

/**
 * Visual flow for the Services hero: platform replaces buyer’s agent,
 * wholesale rates, lender matching, offer tools.
 */
export function ServicesPlatformDiagram() {
  const theme = useTheme();
  const markerId = useId().replace(/:/g, '');
  const primary =
    theme.palette.mainColors?.primary ?? theme.palette.primary.main;
  const border = theme.palette.divider;
  const fill = theme.palette.action.hover;
  const label = theme.palette.text.primary;
  const sublabel = theme.palette.text.secondary;

  return (
    <Box
      sx={{
        mt: 3,
        maxWidth: 720,
        mx: { xs: 0, md: 0 },
        '& svg': { display: 'block', width: '100%', height: 'auto' },
      }}
    >
      <svg
        viewBox="0 0 720 320"
        role="img"
        aria-labelledby="services-diagram-title"
        xmlns="http://www.w3.org/2000/svg"
      >
        <title id="services-diagram-title">
          Unbeatable platform connects homebuyers and investors to wholesale
          rates, instant lender matching, and offer tools—replacing the
          traditional buyer agent middle layer.
        </title>

        <defs>
          <marker
            id={markerId}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={primary} />
          </marker>
        </defs>

        {/* You */}
        <rect
          x="260"
          y="8"
          width="200"
          height="48"
          rx="10"
          fill={fill}
          stroke={border}
          strokeWidth="1.5"
        />
        <text
          x="360"
          y="32"
          textAnchor="middle"
          fill={label}
          fontSize="15"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          Homebuyers &amp; investors
        </text>
        <text
          x="360"
          y="48"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          You
        </text>

        <line
          x1="360"
          y1="58"
          x2="360"
          y2="88"
          stroke={primary}
          strokeWidth="2"
          markerEnd={`url(#${markerId})`}
        />

        {/* Platform hub */}
        <rect
          x="140"
          y="92"
          width="440"
          height="72"
          rx="12"
          fill={fill}
          stroke={primary}
          strokeWidth="2"
        />
        <text
          x="360"
          y="122"
          textAnchor="middle"
          fill={label}
          fontSize="17"
          fontWeight="700"
          fontFamily="system-ui, sans-serif"
        >
          Unbeatable platform
        </text>
        <text
          x="360"
          y="144"
          textAnchor="middle"
          fill={sublabel}
          fontSize="12"
          fontFamily="system-ui, sans-serif"
        >
          Tech replaces the traditional buyer&apos;s agent layer
        </text>

        {/* Fork */}
        <line
          x1="360"
          y1="164"
          x2="360"
          y2="188"
          stroke={primary}
          strokeWidth="2"
        />
        <line
          x1="120"
          y1="188"
          x2="600"
          y2="188"
          stroke={primary}
          strokeWidth="2"
        />
        <line
          x1="120"
          y1="188"
          x2="120"
          y2="208"
          stroke={primary}
          strokeWidth="2"
          markerEnd={`url(#${markerId})`}
        />
        <line
          x1="360"
          y1="188"
          x2="360"
          y2="208"
          stroke={primary}
          strokeWidth="2"
          markerEnd={`url(#${markerId})`}
        />
        <line
          x1="600"
          y1="188"
          x2="600"
          y2="208"
          stroke={primary}
          strokeWidth="2"
          markerEnd={`url(#${markerId})`}
        />

        {/* Three outcomes */}
        <rect
          x="40"
          y="212"
          width="160"
          height="88"
          rx="10"
          fill={fill}
          stroke={border}
          strokeWidth="1.5"
        />
        <text
          x="120"
          y="242"
          textAnchor="middle"
          fill={label}
          fontSize="13"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          Wholesale rates
        </text>
        <text
          x="120"
          y="262"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          Direct access,
        </text>
        <text
          x="120"
          y="278"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          fewer middlemen
        </text>

        <rect
          x="280"
          y="212"
          width="160"
          height="88"
          rx="10"
          fill={fill}
          stroke={border}
          strokeWidth="1.5"
        />
        <text
          x="360"
          y="242"
          textAnchor="middle"
          fill={label}
          fontSize="13"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          Lender matching
        </text>
        <text
          x="360"
          y="262"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          Instant routing to
        </text>
        <text
          x="360"
          y="278"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          330+ lenders
        </text>

        <rect
          x="520"
          y="212"
          width="160"
          height="88"
          rx="10"
          fill={fill}
          stroke={border}
          strokeWidth="1.5"
        />
        <text
          x="600"
          y="238"
          textAnchor="middle"
          fill={label}
          fontSize="13"
          fontWeight="600"
          fontFamily="system-ui, sans-serif"
        >
          Offer &amp; negotiate
        </text>
        <text
          x="600"
          y="258"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          Built-in tools for
        </text>
        <text
          x="600"
          y="274"
          textAnchor="middle"
          fill={sublabel}
          fontSize="11"
          fontFamily="system-ui, sans-serif"
        >
          contracts &amp; deals
        </text>
      </svg>
    </Box>
  );
}
